import { useState, useMemo } from "react";
import { router } from "@inertiajs/react";
import { X, Filter, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import type { FilterOptions, CurrentFilters } from "@/types/generated";

interface TrackEventFiltersProps {
  filterOptions: FilterOptions;
  currentFilters: CurrentFilters;
  userId: string;
}

export function TrackEventFilters({
  filterOptions,
  currentFilters,
  userId,
}: TrackEventFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [sessionTypesOpen, setSessionTypesOpen] = useState(false);

  // Count active filters
  const activeFilterCount = Object.keys(currentFilters || {}).filter(
    (key) => key !== "sort_by"
  ).length;

  // Handle filter changes
  const updateFilters = (updates: Partial<CurrentFilters>) => {
    const newFilters = { ...currentFilters, ...updates };

    // Remove empty/null values
    Object.keys(newFilters).forEach((key) => {
      const value = newFilters[key as keyof CurrentFilters];
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        delete newFilters[key as keyof CurrentFilters];
      }
    });

    console.log("Updating filters:", newFilters);

    router.get(
      `/profile/${userId}`,
      { ...newFilters, page: 1 }, // Reset to page 1 when filters change
      { preserveState: true, preserveScroll: false }
    );
  };

  const clearAllFilters = () => {
    router.get(
      `/profile/${userId}`,
      {},
      { preserveState: true, preserveScroll: false }
    );
  };

  const clearFilter = (key: keyof CurrentFilters) => {
    const newFilters = { ...currentFilters };
    delete newFilters[key];

    // Don't use updateFilters here to avoid passing partial object
    router.get(
      `/profile/${userId}`,
      { ...newFilters, page: 1 },
      { preserveState: true, preserveScroll: false }
    );
  };

  // Get selected car/track names for display
  const selectedCar = filterOptions.sim_cars.find(
    (car) => car.id === currentFilters?.by_sim_car
  );
  const selectedTrack = filterOptions.tracks.find(
    (track) => track.id === currentFilters?.by_track
  );

  // Prepare car options for combobox with grouping by simulator
  const carOptions: ComboboxOption[] = useMemo(
    () =>
      filterOptions.sim_cars.map((car) => ({
        value: car.id.toString(),
        label: car.name,
        group: car.simulator.toUpperCase(),
      })),
    [filterOptions.sim_cars]
  );

  // Prepare track options for combobox with grouping by country
  const trackOptions: ComboboxOption[] = useMemo(
    () =>
      filterOptions.tracks.map((track) => ({
        value: track.id.toString(),
        label: track.name,
        group: track.country || "Other",
      })),
    [filterOptions.tracks]
  );

  // Get active date range filter
  const activeDateRange = currentFilters?.last_7_days
    ? "last_7_days"
    : currentFilters?.last_30_days
      ? "last_30_days"
      : currentFilters?.last_90_days
        ? "last_90_days"
        : currentFilters?.this_year
          ? "this_year"
          : "all_time";

  return (
    <div className="space-y-4">
      {/* Filter Toggle Button */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-2"
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
              {activeFilterCount}
            </span>
          )}
        </Button>

        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-muted-foreground"
          >
            Clear all
          </Button>
        )}
      </div>

      {/* Active Filters Pills */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedCar && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
              Car: {selectedCar.name}
              <X
                className="h-3 w-3 cursor-pointer hover:text-gray-900"
                onClick={() => clearFilter("by_sim_car")}
              />
            </span>
          )}
          {selectedTrack && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
              Track: {selectedTrack.name}
              <X
                className="h-3 w-3 cursor-pointer hover:text-gray-900"
                onClick={() => clearFilter("by_track")}
              />
            </span>
          )}
          {currentFilters?.by_session_types &&
            currentFilters.by_session_types.length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
                Sessions:{" "}
                {currentFilters.by_session_types
                  .map((type) => {
                    const sessionType = filterOptions.session_types.find(
                      (st) => st.value === type
                    );
                    return sessionType?.label || type;
                  })
                  .join(", ")}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-gray-900"
                  onClick={() => clearFilter("by_session_types")}
                />
              </span>
            )}
          {activeDateRange !== "all_time" && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
              {activeDateRange.replace(/_/g, " ")}
              <X
                className="h-3 w-3 cursor-pointer hover:text-gray-900"
                onClick={() => {
                  if (currentFilters?.last_7_days) clearFilter("last_7_days");
                  if (currentFilters?.last_30_days) clearFilter("last_30_days");
                  if (currentFilters?.last_90_days) clearFilter("last_90_days");
                  if (currentFilters?.this_year) clearFilter("this_year");
                }}
              />
            </span>
          )}
        </div>
      )}

      {/* Filter Controls */}
      {showFilters && (
        <div className="space-y-4 p-4 border rounded-lg bg-white">
          {/* Filters Row */}
          <div className="flex flex-wrap gap-4">
            {/* Sort Order */}
            <div className="w-fit space-y-2">
              <label className="text-sm font-medium">Sort by</label>
              <Select
                value={currentFilters?.sort_by || "date_desc"}
                onValueChange={(value) => {
                  console.log("Sort value changed to:", value);
                  updateFilters({ sort_by: value });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sort order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Date (newest first)</SelectItem>
                  <SelectItem value="date_asc">Date (oldest first)</SelectItem>
                  <SelectItem value="finish_position">
                    Best finish position
                  </SelectItem>
                  <SelectItem value="updated_at">Recently updated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Car Filter */}
            <div className="w-fit min-w-[200px] space-y-2">
              <label className="text-sm font-medium">
                Car {carOptions.length > 0 && `(${carOptions.length})`}
              </label>
              <Combobox
                options={carOptions}
                value={currentFilters?.by_sim_car?.toString()}
                onValueChange={(value) => {
                  updateFilters({
                    by_sim_car: value ? parseInt(value) : undefined,
                  });
                }}
                placeholder="All cars"
                searchPlaceholder="Search cars..."
                emptyText="No cars found."
              />
            </div>

            {/* Track Filter */}
            <div className="w-fit min-w-[200px] space-y-2">
              <label className="text-sm font-medium">
                Track {trackOptions.length > 0 && `(${trackOptions.length})`}
              </label>
              <Combobox
                options={trackOptions}
                value={currentFilters?.by_track?.toString()}
                onValueChange={(value) => {
                  updateFilters({
                    by_track: value ? parseInt(value) : undefined,
                  });
                }}
                placeholder="All tracks"
                searchPlaceholder="Search tracks..."
                emptyText="No tracks found."
              />
            </div>

            {/* Session Type Filter */}
            <div className="w-fit space-y-2">
              <label className="text-sm font-medium">Session Type</label>
              <Popover
                open={sessionTypesOpen}
                onOpenChange={setSessionTypesOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {currentFilters?.by_session_types &&
                      currentFilters.by_session_types.length > 0
                      ? `${currentFilters.by_session_types.length} selected`
                      : "All sessions"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-4">
                  <div className="space-y-2">
                    {filterOptions.session_types.map((type) => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`session-${type.value}`}
                          checked={
                            currentFilters?.by_session_types?.includes(type.value) ||
                            false
                          }
                          onCheckedChange={(checked) => {
                            const current =
                              currentFilters?.by_session_types || [];
                            const updated = checked
                              ? [...current, type.value]
                              : current.filter((t) => t !== type.value);
                            updateFilters({
                              by_session_types:
                                updated.length > 0 ? updated : undefined,
                            });
                          }}
                        />
                        <label
                          htmlFor={`session-${type.value}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize cursor-pointer"
                        >
                          {type.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="w-fit space-y-2">
            <label className="text-sm font-medium">Date Range</label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeDateRange === "all_time" ? "default" : "outline"}
                size="sm"
                onClick={() =>
                  updateFilters({
                    last_7_days: undefined,
                    last_30_days: undefined,
                    last_90_days: undefined,
                    this_year: undefined,
                  })
                }
              >
                All Time
              </Button>
              <Button
                variant={
                  activeDateRange === "last_7_days" ? "default" : "outline"
                }
                size="sm"
                onClick={() =>
                  updateFilters({
                    last_7_days: true,
                    last_30_days: undefined,
                    last_90_days: undefined,
                    this_year: undefined,
                  })
                }
              >
                Last 7 Days
              </Button>
              <Button
                variant={
                  activeDateRange === "last_30_days" ? "default" : "outline"
                }
                size="sm"
                onClick={() =>
                  updateFilters({
                    last_7_days: undefined,
                    last_30_days: true,
                    last_90_days: undefined,
                    this_year: undefined,
                  })
                }
              >
                Last 30 Days
              </Button>
              <Button
                variant={
                  activeDateRange === "last_90_days" ? "default" : "outline"
                }
                size="sm"
                onClick={() =>
                  updateFilters({
                    last_7_days: undefined,
                    last_30_days: undefined,
                    last_90_days: true,
                    this_year: undefined,
                  })
                }
              >
                Last 90 Days
              </Button>
              <Button
                variant={
                  activeDateRange === "this_year" ? "default" : "outline"
                }
                size="sm"
                onClick={() =>
                  updateFilters({
                    last_7_days: undefined,
                    last_30_days: undefined,
                    last_90_days: undefined,
                    this_year: true,
                  })
                }
              >
                This Year
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
